/** Shared by the bundle-payment pages (CartCheckout, CartConfirmation). */

/** Matches cart-checkout's MAX_CART_INVOICES. */
export const MAX_CART_INVOICES = 20;

/** Invoice ids from ?invoices=a,b,c -- distinct, non-empty, capped. */
export function parseCartInvoiceIds(raw: string | null): string[] {
  const ids = (raw ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  return [...new Set(ids)].slice(0, MAX_CART_INVOICES);
}

export type CartStatus = 'open' | 'partially_funded' | 'settled' | 'expired' | 'cancelled';
export type CartChargeStatus = 'pending' | 'processing' | 'succeeded' | 'failed' | 'canceled' | null;

/** A cart is done changing once it settled, failed, or is an ACH debit left to clear. */
export function isCartFinal(cart: { status: CartStatus; charge_status: CartChargeStatus }): boolean {
  return cart.status === 'settled' || cart.status === 'cancelled' || cart.charge_status === 'processing' ||
    cart.charge_status === 'failed' || cart.charge_status === 'canceled';
}

/** The fields that decide whether an invoice can go in the cart. */
export interface DueNowCandidate {
  id: string;
  status: string;
  total_amount: number;
  amount_paid: number;
  due_date: string;
  payments?: Array<{ status: string }> | null;
}

export function balanceDueCents(invoice: Pick<DueNowCandidate, 'total_amount' | 'amount_paid'>): number {
  return Math.round(((invoice.total_amount ?? 0) - (invoice.amount_paid ?? 0)) * 100);
}

/**
 * Whether money is already on its way for this invoice: a payment of its own not yet settled or
 * failed, or a bank bundle payment whose ACH debit is still clearing -- which writes no payments
 * row until the money lands, 3-5 business days later. `heldByCart` is useCartHeldInvoiceIds().
 * Every "processing" badge and every "can this be paid again?" check goes through here.
 */
export function isPaymentInFlight(
  invoice: { id: string; payments?: Array<{ status: string }> | null },
  heldByCart: ReadonlySet<string>,
): boolean {
  return heldByCart.has(invoice.id) ||
    (invoice.payments ?? []).some((p) => ['pending', 'created', 'processing'].includes((p.status ?? '').toLowerCase()));
}

/**
 * Whether an invoice belongs in the cart: owed, not paid or voided, no payment in flight (its
 * own, or an ACH bundle payment still settling), and not part-paid under a live Zelle code (the
 * rest of that money belongs under the code). The Payments banner and the cart page both use
 * this, so they always agree on what is due.
 */
export function isDueNow(
  invoice: DueNowCandidate,
  heldByCart: Set<string>,
  liveZelle: Map<string, { partlyFunded: boolean }>,
): boolean {
  const status = (invoice.status ?? '').toLowerCase();
  if (status === 'paid' || status === 'voided') return false;
  if (balanceDueCents(invoice) <= 0) return false;
  if (isPaymentInFlight(invoice, heldByCart)) return false;
  return !liveZelle.get(invoice.id)?.partlyFunded;
}

/** Oldest due first; ties by id so the order never depends on how the rows arrived. */
export function compareDueNow(a: Pick<DueNowCandidate, 'id' | 'due_date'>, b: Pick<DueNowCandidate, 'id' | 'due_date'>): number {
  return (a.due_date ?? '').localeCompare(b.due_date ?? '') || a.id.localeCompare(b.id);
}
